import React from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { PowerFunnelGatedFeatures } from '../../../../Analytics/constants';
import useCollegeProspectAccess from '../../../../hooks/useCollegeProspectAccess';
import Img from '../../../../Img';
import styles from '../../collegeProspectTab.scss';

const TESTID_PREFIX = 'profile.college-prospect.upgrade-banner';
const SUBSCRIPTIONS_PATH = '/subscriptions';

export const CPP_UPGRADE_SUBTITLE = 'Unlock the tools and insights you need to take control of your college recruiting journey!';

export const CPP_UPGRADE_FEATURES = [
    {
        key: 'college-fit-tool',
        iconSrc: '/img/v2/icons/icon-grad-hat.png',
        title: 'College Fit Tool',
        description: 'Get recommendations for colleges that are a best fit for you based on your UTR Rating.',
    },
    {
        key: 'custom-target-list',
        iconSrc: '/img/v2/icons/icon-clipboard.png',
        title: 'Custom College Target List',
        description: 'Create and manage your college list and notify coaches of your interest.',
    },
    {
        key: 'connect-with-coaches',
        iconSrc: '/img/v2/icons/icon-chat-bubble.png',
        title: 'Connect with College Coaches',
        description: 'See which coaches view your profile and message directly with schools you’re targeting.',
    },
];

export const CPP_UPGRADE_OTHER_BENEFITS_FEATURE = {
    key: 'other-power-benefits',
    iconSrc: '/img/v3/icons/icon-utr-logo-black.svg',
    title: 'Other Power Benefits',
    description: '2 Decimal ratings, in-depth performance analytics, discounted entry fees, & more.',
};

const UpgradeToAnnualBanner = () => {
    const { isPowered, hasAnnualPower, openUpgradePopup } = useCollegeProspectAccess();
    const hasPastDueSubscription = useSelector((state) => !!state.auth?.user?.hasPastDueSubscription);

    if (hasAnnualPower) {
        return null;
    }

    // Past-due members resume their existing subscription instead of buying a new one;
    // free members see the "Get Annual Power" acquisition copy with the extra Power-benefits
    // bullet; Power Monthly members see the "Upgrade to Annual" copy.
    const isFree = !isPowered;
    const getBannerCopy = () => {
        if (hasPastDueSubscription) {
            return { title: 'Resume Power', ctaText: 'RESUME POWER' };
        }
        if (isFree) {
            return { title: 'Get Annual Power', ctaText: 'GET POWER' };
        }
        return { title: 'Upgrade to Annual Power', ctaText: 'UPGRADE TO ANNUAL' };
    };
    const { title, ctaText } = getBannerCopy();
    const features = isFree
        ? [...CPP_UPGRADE_FEATURES, CPP_UPGRADE_OTHER_BENEFITS_FEATURE]
        : CPP_UPGRADE_FEATURES;

    const handleUpgradeClick = () => {
        openUpgradePopup(PowerFunnelGatedFeatures.CPP_GET_POWER_CTA);
    };

    return (
        <div
            className={styles.upgradeBanner}
            data-testid={`${TESTID_PREFIX}.container`}
        >
            <div
                className={styles.upgradeBannerHeader}
                data-testid={`${TESTID_PREFIX}.header`}
            >
                {title}
            </div>
            <div
                className={styles.upgradeBannerBody}
                data-testid={`${TESTID_PREFIX}.body`}
            >
                <div
                    className={styles.upgradeBannerSubtitle}
                    data-testid={`${TESTID_PREFIX}.subtitle`}
                >
                    {CPP_UPGRADE_SUBTITLE}
                </div>
                <div
                    className={styles.upgradeBannerFeatures}
                    data-testid={`${TESTID_PREFIX}.features`}
                >
                    {features.map((feature) => (
                        <div
                            key={feature.key}
                            className={styles.upgradeBannerFeature}
                            data-testid={`${TESTID_PREFIX}.features.${feature.key}`}
                        >
                            <Img
                                src={feature.iconSrc}
                                alt={feature.title}
                                className={styles.upgradeBannerFeatureIcon}
                            />
                            <div className={styles.upgradeBannerFeatureText}>
                                <div
                                    className={styles.upgradeBannerFeatureTitle}
                                    data-testid={`${TESTID_PREFIX}.features.${feature.key}.title`}
                                >
                                    {feature.title}
                                </div>
                                <div
                                    className={styles.upgradeBannerFeatureDescription}
                                    data-testid={`${TESTID_PREFIX}.features.${feature.key}.description`}
                                >
                                    {feature.description}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                <div className={styles.upgradeBannerCta}>
                    {hasPastDueSubscription ? (
                        <Link
                            to={SUBSCRIPTIONS_PATH}
                            className={styles.upgradeBannerCtaButton}
                            data-testid={`${TESTID_PREFIX}.cta-btn`}
                        >
                            {ctaText}
                        </Link>
                    ) : (
                        <button
                            type="button"
                            className={styles.upgradeBannerCtaButton}
                            onClick={handleUpgradeClick}
                            data-testid={`${TESTID_PREFIX}.cta-btn`}
                        >
                            {ctaText}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default UpgradeToAnnualBanner;
